import { useEffect, useMemo, useState } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { NoteCard } from '@/components/NoteCard';

/**
 * Masonry layout that is SAFE for infinite scroll.
 *
 * CSS multi-column (`columns-*`) re-balances the entire set of items whenever
 * anything is added, which — with hundreds of notes and lazily-loading images —
 * causes cards to visually jump, reflow, and effectively get "lost" as you
 * scroll. Instead we distribute notes round-robin into a fixed number of
 * flexbox columns. Appending a new page only adds cards to the bottom of each
 * column; nothing above ever reflows.
 */
function useColumnCount(): number {
  const getCount = () => {
    if (typeof window === 'undefined') return 3;
    const w = window.innerWidth;
    if (w < 640) return 1; // < sm
    if (w < 1024) return 2; // < lg
    return 3;
  };

  const [count, setCount] = useState(getCount);

  useEffect(() => {
    const onResize = () => setCount(getCount());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return count;
}

export function MasonryGrid({ notes }: { notes: NostrEvent[] }) {
  const columnCount = useColumnCount();

  // Distribute notes round-robin so each column grows independently and in a
  // stable order (card N always lands in column N % columnCount).
  const columns = useMemo(() => {
    const cols: NostrEvent[][] = Array.from({ length: columnCount }, () => []);
    notes.forEach((note, i) => {
      cols[i % columnCount].push(note);
    });
    return cols;
  }, [notes, columnCount]);

  return (
    <div className="flex gap-5 items-start">
      {columns.map((col, i) => (
        <div key={i} className="flex-1 min-w-0 flex flex-col gap-5">
          {col.map(note => (
            <NoteCard key={note.id} event={note} />
          ))}
        </div>
      ))}
    </div>
  );
}
